import { get, size } from 'global/lib/lodash';

// Calculates the display of a score based on scorecard config
export const getDisplayScoreLabel = (opt, i, scorecardConfig = null, isOption = false) => {
  if (!scorecardConfig) {
    return get(opt, 'value', i);
  } else {
    const val = get(opt, 'value');

    if (val === null || isNaN(val)) {
      return 'N/A';
    }

    // If a label is set previously, use it -- this ensures that label overrides from the sc are used
    const buttonLabel = get(opt, 'label');
    if (!!buttonLabel && typeof buttonLabel === 'string') {
      return buttonLabel;
    }
    if (val === -1) {
      return 'N/O';
    }

    const optionValue = get(
      scorecardConfig?.scoreValueLabel?.find((f) => (f.value === val)),
      'label'
    );
    if (optionValue) {
      return optionValue;
    }

    const maxScore = parseFloat(scorecardConfig.maxScore);
    const minScore = parseFloat(scorecardConfig.minScore);
    const displayPercentage = get(scorecardConfig, 'displayPercentage', true);
    const displayCountType = get(scorecardConfig, 'displayCountType','average');

    // For "Count Up" and "Count Down" scoring, we don't care about min/max or percentage config values; only weight
    if (displayCountType !== "average") {
      let weight = get(opt, 'weight', 1);
      const weightOverrides = get(scorecardConfig,'weightOverrides');
      if (weight === 1 && weightOverrides && get(opt,'checklistId')) {
        const checklistWeight = weightOverrides.filter((w) => w.type === 'Checklist' && w.id === get(opt,'checklistId',0).toString());
        const systemWeight = weightOverrides.filter((w) => w.type === 'Superset' && w.id === get(opt,'supersetId',0).toString());
        if (size(checklistWeight) > 0 || size(systemWeight) > 0) {
          weight = weight * get(checklistWeight, '0.value', 1) * get(systemWeight,'0.value',1);
        }
      }
      let scoreVal = parseFloat(((val / 100)).toFixed(4)) * weight;
      if (displayCountType === 'count_down') {
        scoreVal = scoreVal - weight;
      }
      // simple values can have as many as 2 decimal places
      return Math.round(scoreVal*100)/100;
    } else if (displayPercentage) {
      return parseFloat(val).toFixed(0) + '%';
    } else {
      const scoreVal = parseFloat(((val / 100) * (maxScore - minScore) + minScore).toFixed(4));
      // To display an exact option value, sometimes we need to round instead of showing the exact calculated value
      // This is to account for the possibility of values in the db that don't precisely correspond to real options,
      // because the database stores all option values as integers.
      //   For example: 33 is a stand-in value for 33.3333 (as 67 is for 66.666666) in a scorecard with display scores of 1-4
      //   In this example, this logic will ensure we display 2 (instead of 1.99 = 33/100 * (4 - 1) + 1)
      //   And 3 (instead of 3.01 = 67/100 * (4 - 1) + 1)
      // However, we only want to do this rounding when explicitly displaying an _option_ and not to change calculated scores.
      return isOption && Math.abs(maxScore - minScore) > 1 ? Math.round(scoreVal) : scoreVal;
    }
  }
};

// Used to display values in situations separated from the scoring, like a large-screen photo caption or a task
export const getDisplayValue = (opt, scorecardConfig = null) => {
  if (!scorecardConfig) {
    return '';
  }
  return get(scorecardConfig, 'maxScore') === 1 && get(scorecardConfig, 'minScore') === 0
    ? Math.round(get(opt, 'value')) === 100
      ? 'PASS'
      : get(opt, 'value') === -1
        ? 'N/O'
        : 'FAIL'
    : getDisplayScoreLabel(opt, get(opt, 'value'), scorecardConfig);
};

export const getHeatchartScore = (opt, config = {}, slide = [0, 100]) => {
  const max_score = parseFloat(get(config, 'max_score', 1)) || 1;
  const min_score = parseFloat(get(config, 'min_score', 0)) || 0;
  const val = get(opt, 'value', 0);
  const toFix = max_score > 1 ? 2 : 0;
  const configrange = [min_score || 0, max_score || 1].map((p) => parseInt(p));
  const isPercent = configrange[1] == 1;

  if (isNaN(val)) {
    return 'N/A';
  } else if (val === -1) {
    return 'N/O';
  }

  let v = ((val / (isPercent ? 1 : 100)) * (max_score - min_score) + min_score) * get(opt, 'weight', 1);

  // Don't display a max value unless it's a perfect max
  //   i.e. if it would round up to max, floor the value instead
  if (v !== max_score && v.toFixed(toFix) === (toFix > 0 ? max_score.toFixed(toFix) : '100')) {
    const divisor = toFix > 0 ? 100 : 1;
    v = Math.trunc(v * divisor) / divisor;
  }

  const hasRange = slide[0] !== 0 || slide[1] !== 100;

  if (hasRange && !isPercent) {
    const diff = configrange[1] - configrange[0];
    const divi = isPercent ? 1 : 100;
    const [min, max] = [(slide[0] / divi) * diff + configrange[0], (slide[1] / divi) * diff + configrange[0]];
    if (v > max) {
      v = max;
    } else if (v < min) {
      v = min;
    }
  }

  return `${v.toFixed(toFix)}${isPercent ? '%' : ''}`;
};

export default getDisplayScoreLabel;
